import { useState, useEffect } from 'react';

import DateRangePicker from '../common/DateRangePicker';
import Dropdown from '../common/Dropdown';

import useQueryParams from '../../hooks/useQueryParams';

import { ArrowPathIcon } from '@heroicons/react/24/outline';

/**
 * Available granularity options for the report
 * @type {import('../common/Dropdown').DropdownOption[]}
 */
export const GRANULARITY_OPTIONS = Object.freeze([
  { value: 'HOUR', label: 'Hourly' },
  { value: 'DAY', label: 'Daily' },
  { value: 'WEEK', label: 'Weekly' },
  { value: 'MONTH', label: 'Monthly' }
]);

/**
 * @typedef {Object} ReportControlsProps
 * @property {() => void} onRunReport - Function to call when run report button is clicked
 * @property {boolean} isLoading - Whether the report is currently loading
 */

/**
 * Component for managing report execution controls and granularity selection
 * @param {ReportControlsProps} props - The component props
 * @returns {JSX.Element} The rendered report controls
 */
export default function ReportControls({ onRunReport, isLoading }) {
  const { setQueryParam, setBulkQueryParams, getParam } = useQueryParams();
  const currentGranularity = getParam('granularity') || 'DAY';
  const [splitByZone, setSplitByZone] = useState(
    () => getParam('splitByZone') === 'true'
  );

  // Get today's date in YYYY-MM-DD format for max date validation
  const today = new Date().toISOString().split('T')[0];

  // Calculate default "this month" range
  const getDefaultDateRange = () => {
    const now = new Date();
    // Set the date to first day of current month at 00:00:00
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    firstDayOfMonth.setHours(0, 0, 0, 0);

    // Format dates ensuring we use local timezone
    const fromDate =
      firstDayOfMonth.getFullYear() +
      '-' +
      String(firstDayOfMonth.getMonth() + 1).padStart(2, '0') +
      '-' +
      String(firstDayOfMonth.getDate()).padStart(2, '0');

    // Set today to end of day in local timezone
    const todayDate = new Date();
    todayDate.setHours(23, 59, 59, 999);
    const toDate =
      todayDate.getFullYear() +
      '-' +
      String(todayDate.getMonth() + 1).padStart(2, '0') +
      '-' +
      String(todayDate.getDate()).padStart(2, '0');

    return {
      fromDate,
      toDate
    };
  };

  // Initialize date values using state
  const [fromDate, setFromDate] = useState(getParam('fromDate'));
  const [toDate, setToDate] = useState(getParam('toDate'));

  // Set default date range on component mount
  useEffect(() => {
    const { fromDate: defaultFromDate, toDate: defaultToDate } =
      getDefaultDateRange();
    setBulkQueryParams({
      fromDate: defaultFromDate,
      toDate: defaultToDate
    });
    setFromDate(defaultFromDate);
    setToDate(defaultToDate);
  }, []); // Empty dependency array means this runs once on mount

  /**
   * Handles changes to the granularity dropdown
   * @param {React.ChangeEvent<HTMLSelectElement>} event - The change event
   */
  function handleGranularityChange(event) {
    setQueryParam('granularity', event.target.value || 'DAY');
  }

  /**
   * Handles changes to the date range
   * @param {string} newFromDate - The new from date
   * @param {string} newToDate - The new to date
   */
  function handleDateRangeChange(newFromDate, newToDate) {
    setBulkQueryParams({
      toDate: newToDate,
      fromDate: newFromDate
    });

    setToDate(newToDate);
    setFromDate(newFromDate);
  }

  /**
   * Handles changes to the split by zone toggle
   * @param {React.ChangeEvent<HTMLInputElement>} event - The change event
   */
  function handleSplitByZoneChange(event) {
    const isChecked = event.target.checked;
    setSplitByZone(isChecked);
    setQueryParam('splitByZone', isChecked.toString());
  }

  return (
    <div className='flex flex-col sm:flex-row items-center justify-between w-full'>
      <div className="flex flex-col gap-3 sm:flex-row sm:gap-2.5 sm:items-end sm:flex-wrap w-full">
        <DateRangePicker
          label="Date Range"
          startDate={fromDate}
          endDate={toDate}
          onChange={handleDateRangeChange}
          maxDate={today}
          minDate="2025-04-01"
        />

        <Dropdown
          value={currentGranularity}
          options={GRANULARITY_OPTIONS}
          onChange={handleGranularityChange}
          label="Frequency"
          minWidth="120px"
        />

        <div className="flex flex-col">
          <label className="label py-0.5">
            <span className="label-text text-xs text-gray-600 font-[600]">
              Sort by
            </span>
          </label>
          <label className="label cursor-pointer h-9 px-3 py-5 bg-base-100 rounded-btn border border-gray-400 hover:border-base-content/40">
            <span className="text-sm pr-2.5">Zone Position</span>
            <input
              type="checkbox"
              className="toggle toggle-sm"
              checked={splitByZone}
              onChange={handleSplitByZoneChange}
            />
          </label>
        </div>
      </div>

      <button
        className="flex w-full sm:w-fit justify-center sm:justify-between items-center rounded-full bg-[#93C01F] hover:bg-[#9CBF40] py-2.5 px-9 mt-5 sm:mt-3 text-lg text-white uppercase font-semibold whitespace-nowrap"
        onClick={onRunReport}
        disabled={isLoading}
      >
        <ArrowPathIcon
          className={`h-4 w-4 text-bold ${isLoading ? 'ml-1 animate-spin' : 'hidden'}`}
        />
        Run Report
      </button>

    </div>
  );
}
